import dayjs from "dayjs";
import { colors } from "../../theme/colors";
import type {
  SpecimenAuditAnalyticsParams,
  SpecimenAuditEvent,
  SpecimenAuditEventsParams,
  SpecimenAuditSeverity,
} from "../../services/api";

// Status values are spaced across the API and the rest of the FE — see
// requestFormStatus in utils/constants ("READY FOR PICKUP"). Some payloads still
// carry the underscored enum name, so normalise before every lookup and send the
// spaced form back in current_status.
export const normaliseStatus = (statusName?: string | null): string =>
  (statusName ?? "").replace(/_/g, " ").trim().toUpperCase();

export const STATUS_LABELS = [
  "IN OT DROP OFF PENDING",
  "READY FOR PICKUP",
  "IN TRANSIT",
  "RECEIVED BY LAB",
  "NO SPECIMEN",
];

export const statusDisplayLabel = (statusName?: string | null): string =>
  normaliseStatus(statusName);

// Filter menu options: the spaced value is what current_status expects.
export const STATUS_TRANSITION_OPTIONS = STATUS_LABELS.map((label) => ({
  value: label,
  label,
}));

// Shared status -> color map. Pastel background + dark text, matching the
// specimen listing's status chip (Specimens.tsx uses colors.lightGreen for
// READY FOR PICKUP and colors.modalBackground otherwise). Tracking needs to
// tell all five statuses apart — a transition renders two chips at once — so
// the remaining states extend that palette rather than sharing one colour.
export const STATUS_COLOR_MAP: Record<string, string> = {
  "IN OT DROP OFF PENDING": colors.lighterGrey,
  "READY FOR PICKUP": colors.lightGreen,
  "IN TRANSIT": colors.modalBackground,
  "RECEIVED BY LAB": colors.lightBlue,
  "NO SPECIMEN": colors.lightRed,
};

export const statusColor = (statusName?: string | null): string =>
  STATUS_COLOR_MAP[normaliseStatus(statusName)] || colors.lighterGrey;

export const SEVERITY_BORDER_COLOR: Record<SpecimenAuditSeverity, string> = {
  normal: "transparent",
  warning: colors.darkModalBackground,
  breach: colors.error,
};

export const severityBorderColor = (
  severity?: SpecimenAuditSeverity
): string => (severity && SEVERITY_BORDER_COLOR[severity]) || "transparent";

export const DEFAULT_LOOKBACK_HOURS = 24;

export const defaultDateRange = () => {
  const to = dayjs();
  const from = to.subtract(DEFAULT_LOOKBACK_HOURS, "hour");
  return {
    from: from.toISOString(),
    to: to.toISOString(),
  };
};

// The health organisations endpoint is typed `query<any, any>`, so callers get
// no shape from it. This is the subset the tracking screen relies on.
export type HealthOrgOption = { id: number; name: string };

export type SpecimenTrackingFilters = {
  healthOrgId?: number;
  from: string;
  to: string;
  search: string;
  statusTransition: string;
  discrepancy: "" | "YES" | "NO";
  severity: string;
};

export const defaultFilters = (): SpecimenTrackingFilters => ({
  healthOrgId: undefined,
  ...defaultDateRange(),
  search: "",
  statusTransition: "",
  discrepancy: "",
  severity: "",
});

// healthOrgId must be a real tenant id — callers gate on filters.healthOrgId
// being set (there is no "all orgs" mode; the API routes by tenant schema).
export const buildEventsQueryParams = (
  filters: SpecimenTrackingFilters & { healthOrgId: number },
  extra?: Partial<SpecimenAuditEventsParams>
): SpecimenAuditEventsParams => ({
  healthOrgId: filters.healthOrgId,
  from_datetime: filters.from,
  to_datetime: filters.to,
  search: filters.search || undefined,
  current_status: filters.statusTransition || undefined,
  has_discrepancy: filters.discrepancy
    ? filters.discrepancy === "YES"
    : undefined,
  severity: (filters.severity || undefined) as
    | SpecimenAuditEventsParams["severity"]
    | undefined,
  ...extra,
});

export const buildAnalyticsQueryParams = (
  filters: SpecimenTrackingFilters & { healthOrgId: number }
): SpecimenAuditAnalyticsParams => ({
  healthOrgId: filters.healthOrgId,
  from_datetime: filters.from,
  to_datetime: filters.to,
  search: filters.search || undefined,
  current_status: filters.statusTransition || undefined,
  has_discrepancy: filters.discrepancy
    ? filters.discrepancy === "YES"
    : undefined,
  severity: (filters.severity || undefined) as
    | SpecimenAuditEventsParams["severity"]
    | undefined,
});

const CSV_EXPORT_ROW_CAP = 1000;

// Prefix a leading =, +, -, or @ with an apostrophe so Excel/Sheets treat the
// cell as text instead of evaluating it as a formula (CSV formula injection).
const FORMULA_LEADING_CHAR = /^[=+\-@]/;

const csvEscape = (value: unknown): string => {
  let str = value === null || value === undefined ? "" : String(value);
  if (FORMULA_LEADING_CHAR.test(str)) {
    str = `'${str}`;
  }
  if (/[",\n]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

// The "# ..." preamble lines are single-line comments, not CSV fields, so
// csvEscape is the wrong tool: it would keep an embedded newline inside quotes
// and the continuation would land unprefixed and parse as a data row. Freeform
// input (search text, org names) is flattened to one line and its quotes
// neutralised so it cannot terminate the value or forge extra rows.
const metadataValue = (value: unknown): string =>
  (value === null || value === undefined ? "" : String(value))
    .replace(/[\r\n]+/g, " ")
    .replace(/"/g, "'")
    .trim();

export const eventsToCsvRows = (events: SpecimenAuditEvent[]): string[] => {
  const header = [
    "occurred_at",
    "specimen_qr",
    "bag_qr",
    "request_form_id",
    "status_from",
    "status_to",
    "actor_email",
    "action",
    "reason",
    "comments",
    "is_discrepancy",
    "severity",
    "is_hidden_recalculation",
    "evidence_url",
  ];

  const rows = events.map((event) =>
    [
      event.occurred_at,
      event.specimen_qr,
      event.bag_qr,
      event.request_form_id,
      statusDisplayLabel(event.status_transition_from),
      statusDisplayLabel(event.status_transition_to),
      event.actor_email,
      event.action,
      event.reason,
      event.comments,
      event.is_discrepancy ? "YES" : "NO",
      event.severity,
      event.is_hidden_recalculation ? "YES" : "NO",
      event.evidence_url,
    ]
      .map(csvEscape)
      .join(",")
  );

  return [header.join(","), ...rows];
};

export type CsvExportOptions = {
  healthOrgLabel: string;
  filters: SpecimenTrackingFilters;
  totalCount: number;
};

export const buildSpecimenAuditCsv = (
  events: SpecimenAuditEvent[],
  { healthOrgLabel, filters, totalCount }: CsvExportOptions
): { csv: string; wasTruncated: boolean } => {
  const wasTruncated = totalCount > CSV_EXPORT_ROW_CAP;
  const rowCount = Math.min(events.length, CSV_EXPORT_ROW_CAP);

  const metadata = [
    `# Health organisation: ${metadataValue(healthOrgLabel)}`,
    `# Date range: ${metadataValue(filters.from)} to ${metadataValue(filters.to)}`,
    `# Generated at: ${new Date().toISOString()}`,
    `# Filters: search="${metadataValue(filters.search)}" status_transition="${metadataValue(
      filters.statusTransition
    )}" discrepancy="${metadataValue(
      filters.discrepancy
    )}" severity="${metadataValue(filters.severity)}"`,
    `# Row count: ${rowCount}${wasTruncated ? ` (truncated from ${totalCount})` : ""}`,
  ];

  const csv = [...metadata, ...eventsToCsvRows(events.slice(0, CSV_EXPORT_ROW_CAP))].join(
    "\n"
  );

  return { csv, wasTruncated };
};

export const downloadCsv = (csv: string, filename: string): void => {
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
};

export { CSV_EXPORT_ROW_CAP };

// --- Redesign helpers -------------------------------------------------------
// Shared severity chip styling so the activity feed and the timeline inspector
// present severity the same way (matches the app's badge conventions).
export type SeverityChipStyle = { bg: string; color: string; label: string };

export const SEVERITY_CHIP_STYLE: Record<SpecimenAuditSeverity, SeverityChipStyle> = {
  normal: { bg: colors.lightGreen, color: colors.success, label: "Normal" },
  warning: { bg: colors.modalBackground, color: "#9A3412", label: "Warning" },
  breach: { bg: colors.lightRed, color: colors.darkerRed, label: "Breach" },
};

export const severityChipStyle = (
  severity?: SpecimenAuditSeverity
): SeverityChipStyle => (severity && SEVERITY_CHIP_STYLE[severity]) || SEVERITY_CHIP_STYLE.normal;

// Options shaped for the shared SingleSelectInput ({ id, label, value }).
export const STATUS_TRANSITION_SELECT_OPTIONS = STATUS_TRANSITION_OPTIONS.map(
  (option, index) => ({ id: index + 1, label: option.label, value: option.value })
);

// No "All" entry — an empty value means "no discrepancy filter" and renders the
// placeholder, matching the status transition and severity selects.
export const DISCREPANCY_SELECT_OPTIONS = [
  { id: 1, label: "Yes", value: "YES" },
  { id: 2, label: "No", value: "NO" },
];

export const SEVERITY_SELECT_OPTIONS = [
  { id: 1, label: "Normal", value: "normal" },
  { id: 2, label: "Warning", value: "warning" },
  { id: 3, label: "Breach", value: "breach" },
];
